/**
 * Hochlevel-Komposition: aus den Daten einer Fallakte heraus den
 * vollständigen `ScoreResult` ableiten.
 *
 * Diese Funktion ist die Orchestrator-Funktion der Score-Engine — alle
 * Sub-Funktionen (`computeXScore`, `computeConfidence`, `getRiskIndicators`)
 * werden hier zusammengeführt. Sie bleibt bewusst „dünn" — die Logik
 * sitzt in den Sub-Modulen.
 *
 * Eingabe ist ein `Partial<Record<DimensionKey, DimensionPayload>>` —
 * fehlende Dimensionen werden NICHT als „0" mitgewichtet, sondern aus
 * dem Total-Score komplett ausgeschlossen. Dadurch ist eine perfekt
 * erfasste Resources-Dimension auch als „100" sichtbar, statt durch
 * leere Defaults nach unten gezogen zu werden.
 *
 * Vollständigkeitsstatus (`state`):
 *   - `unscored`     — keine Dimension touched
 *   - `preliminary`  — mind. eine, aber nicht alle 6 erfasst,
 *                      oder Konfidenz < CONFIDENCE_FINAL_THRESHOLD
 *   - `final`        — alle 6 Dimensionen erfasst UND Konfidenz ≥ Schwelle
 */
import type { Percentage, ScoreValue } from '../schema/brands.js';
import type {
  ActivityData,
  DimensionKey,
  HealthData,
  IdentityData,
  PrivateData,
  ResourcesData,
  StyleData,
} from '../schema/dimensionData.js';
import type { FinancialDataset } from '../schema/financialDataset.js';
import type { ScoreResult, ScoreState } from '../schema/score.js';
import { computeIdentityScore } from './computeIdentityScore.js';
import { computeActivityScore } from './computeActivityScore.js';
import { computeStyleScore } from './computeStyleScore.js';
import { computeResourcesScore } from './computeResourcesScore.js';
import { computeHealthScore } from './computeHealthScore.js';
import { computePrivateScore } from './computePrivateScore.js';
import {
  computeDimensionConfidence,
  computeTotalConfidence,
  CONFIDENCE_FINAL_THRESHOLD,
} from './computeConfidence.js';
import { computeTotalScore } from './computeTotalScore.js';
import { getTrafficLight } from './getTrafficLight.js';
import {
  getActivityRiskIndicators,
  getHealthRiskIndicators,
  getIdentityRiskIndicators,
  getPrivateRiskIndicators,
  getResourcesRiskIndicators,
  getStyleRiskIndicators,
} from './getRiskIndicators.js';

export interface ScoreEngineDimensionInput {
  identity?: IdentityData;
  activity?: ActivityData;
  style?: StyleData;
  resources?: ResourcesData;
  health?: HealthData;
  private?: PrivateData;
}

export interface ScoreEngineInput extends ScoreEngineDimensionInput {
  /** BWA-/EKS-Daten — Hauptgrundlage der Resources-Dimension. */
  financialDataset?: FinancialDataset;
  /** BG-Lücke in Cents (positiv = ungedeckt). Wird vom BG-Modul geliefert. */
  bgGapCents?: number;
  /** Zeitstempel des Berechnungslaufs — injizierbar für Determinismus in Tests. */
  computedAt: Date;
}

const DIMENSION_ORDER: readonly DimensionKey[] = [
  'identity',
  'activity',
  'style',
  'resources',
  'health',
  'private',
];

export function computeScoreResult(input: ScoreEngineInput): ScoreResult {
  const fd = input.financialDataset;

  // Pro Dimension: nur bewerten, wenn die Dimension überhaupt im Input
  // vorhanden ist. Resources ist ein Sonderfall — die Dimension gilt schon
  // als touched, wenn entweder die qualitativen ResourcesData ODER ein
  // FinancialDataset übergeben wurde (BWA-Eingabe ohne Notes ist legitim).
  const isTouched: Record<DimensionKey, boolean> = {
    identity: input.identity !== undefined,
    activity: input.activity !== undefined,
    style: input.style !== undefined,
    resources: input.resources !== undefined || fd !== undefined,
    health: input.health !== undefined,
    private: input.private !== undefined,
  };

  const dimensionScores: Partial<Record<DimensionKey, ScoreValue>> = {};
  const dimensionConfidences: Partial<Record<DimensionKey, Percentage>> = {};
  const riskIndicators = [];

  if (input.identity) {
    dimensionScores.identity = computeIdentityScore(input.identity);
    dimensionConfidences.identity = computeDimensionConfidence(input.identity);
    riskIndicators.push(...getIdentityRiskIndicators(input.identity));
  }
  if (input.activity) {
    dimensionScores.activity = computeActivityScore(input.activity);
    dimensionConfidences.activity = computeDimensionConfidence(input.activity);
    riskIndicators.push(...getActivityRiskIndicators(input.activity));
  }
  if (input.style) {
    dimensionScores.style = computeStyleScore(input.style);
    dimensionConfidences.style = computeDimensionConfidence(input.style);
    riskIndicators.push(...getStyleRiskIndicators(input.style));
  }
  if (isTouched.resources) {
    // Wenn keine ResourcesData vorhanden sind, aber das FinancialDataset
    // existiert, behandeln wir die qualitativen Felder als leer (alle
    // optional) — die Score-Heuristik liest sowieso nur das FD.
    const resData: ResourcesData = input.resources ?? { dimension: 'resources' };
    dimensionScores.resources = computeResourcesScore(fd);
    dimensionConfidences.resources = computeDimensionConfidence(resData, fd);
    riskIndicators.push(...getResourcesRiskIndicators(fd));
  }
  if (input.health) {
    dimensionScores.health = computeHealthScore(input.health);
    dimensionConfidences.health = computeDimensionConfidence(input.health);
    riskIndicators.push(...getHealthRiskIndicators(input.health));
  }
  if (input.private) {
    dimensionScores.private = computePrivateScore(input.private, input.bgGapCents);
    dimensionConfidences.private = computeDimensionConfidence(input.private, fd);
    riskIndicators.push(...getPrivateRiskIndicators(input.private, input.bgGapCents));
  }

  const dimensionsScored = DIMENSION_ORDER.filter((d) => dimensionScores[d] !== undefined);
  const totalScore = computeTotalScore(dimensionScores);
  const totalConfidencePercent = computeTotalConfidence(dimensionConfidences);
  const trafficLight = getTrafficLight(totalScore);

  let state: ScoreState;
  if (dimensionsScored.length === 0) state = 'unscored';
  else if (
    dimensionsScored.length === DIMENSION_ORDER.length &&
    (totalConfidencePercent as number) >= (CONFIDENCE_FINAL_THRESHOLD as number)
  ) {
    state = 'final';
  } else state = 'preliminary';

  return {
    dimensionScores: dimensionsScored.map((d) => ({
      dimension: d,
      // bei dimensionsScored definitiv vorhanden — der Cast ist sicher
      score: dimensionScores[d] as ScoreValue,
      confidencePercent: dimensionConfidences[d] as Percentage,
    })),
    dimensionsScored,
    totalScore,
    totalConfidencePercent,
    trafficLight,
    state,
    riskIndicators,
    computedAt: input.computedAt,
  };
}
